import { useEffect, useRef } from 'react'
import { MicOff, VideoOff } from 'lucide-react'
import type { Peer } from './room'

/**
 * The tiles.
 *
 * ## Nothing enlarges whoever is talking
 *
 * A layout that followed the speaker would rearrange the seating every few seconds and make
 * a meeting look like a broadcast with an audience. The person speaking gets a ring and
 * everybody keeps their place.
 *
 * ## Tracks are re-attached on every render that changes the peers
 *
 * Attaching only in the ref callback races the track becoming available, and the symptom is
 * the one nobody debugs quickly: a person alone in a room turns their camera on and sees
 * nothing. `srcObject` is idempotent, so re-attaching costs nothing.
 */
export function VideoGrid({ peers }: { peers: Peer[] }) {
  const columns = peers.length <= 1 ? 'grid-cols-1'
    : peers.length <= 4 ? 'grid-cols-2'
    : peers.length <= 9 ? 'grid-cols-3' : 'grid-cols-4'

  return (
    <div className={`grid gap-2 ${columns}`}>
      {peers.map((peer) => <Tile key={peer.identity} peer={peer} />)}
    </div>
  )
}

function Tile({ peer }: { peer: Peer }) {
  const ref = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (el.srcObject !== peer.stream) {
      el.srcObject = peer.stream
    }
  }, [peer.stream])

  return (
    <div className={`relative aspect-video overflow-hidden rounded-md bg-neutral-900 ${
      peer.speaking ? 'ring-2 ring-amber-400' : 'ring-1 ring-border'}`}>
      {peer.stream && peer.cameraOn ? (
        <video ref={ref} autoPlay playsInline
          // Muted on the local tile ONLY: unmuted, a participant hears themselves a beat
          // late and the room sounds broken. Remote audio does not play through these
          // elements at all — it is attached outside React; see room.ts.
          muted
          className="size-full object-cover" />
      ) : (
        <div className="flex size-full items-center justify-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-neutral-700
                           text-erp-sm font-medium text-white">
            {initials(peer.name)}
          </span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-black/50 px-2 py-1">
        <span className="truncate text-erp-xs text-white">
          {peer.name}{peer.local ? ' (you)' : ''}
        </span>
        {!peer.micOn && <MicOff className="size-3.5 shrink-0 text-red-400" />}
        {!peer.cameraOn && <VideoOff className="size-3.5 shrink-0 text-red-400" />}
      </div>
    </div>
  )
}

function initials(name: string) {
  return name.split(/\s+/).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || '?'
}
